import { useState } from "react";
import type { StaffAskRow, StaffRequestWhatsappStatus } from "@teamna/shared";
import { useSendStaffAsk, useStaffAsk } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { messageForError } from "../components/CodeFlow.tsx";
import { Button, FormSection, InlineError, SkeletonList, showToast } from "../components/index.ts";
import { formatDayShort } from "@teamna/web-client/lib/format.ts";

/**
 * "Ask coaches to add their swimmers", on the Staff page (strategy repo:
 * teamna/prototypes/coach-adds-swimmers.html). Send gives each coach a private WhatsApp from the
 * Teamna number with the task and the link to My swimmers, and a push in the app. Nothing goes
 * to a WhatsApp group, and parents get nothing.
 *
 * The card is part of the module "Coaches add their swimmers" (Settings, Modules, since 2026-11-03):
 * the Staff page shows it only when the module is on, and the switch is in Settings, not here.
 */
export function AskStaffCard({ businessId }: { businessId: string }) {
  const { label } = useLabels();
  const ask = useStaffAsk(businessId);
  const send = useSendStaffAsk(businessId);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const staff = label("staff", { plural: true });
  const people = label("participant", { plural: true });

  const run = (ids: string[] | undefined, button: string) => {
    setSentTo(button);
    send.mutate(ids ? { staffUserIds: ids } : {}, {
      onSuccess: (answer) => {
        const all = answer.results.length;
        const who = all === 1 ? answer.results[0]!.name : `${all} ${staff}`;
        const sent = answer.results.filter((r) => r.status === "sent").length;
        const pushOnly = answer.results.filter((r) => r.status === "not_configured").length;
        showToast(
          sent === all
            ? `Sent to ${who} on WhatsApp.`
            : pushOnly === all
              ? `Asked ${who}. WhatsApp is not connected here, so they get the push only.`
              : `${sent} sent on WhatsApp. ${all - sent} not sent: see each row.`,
        );
      },
      onError: (error) => showToast(messageForError(error), { tone: "info" }),
    });
  };

  return (
    <FormSection
      title={`Ask ${staff} to add their ${people}`}
      description={`Each ${label("staff")} types the names of the ${people} they teach, under each ${label("group")}. You get one message for each batch. Then you link the ${label("member", { plural: true })} and pick the days.`}
    >
      <div className="flex flex-col gap-4 py-4">
        {ask.isLoading ? (
          <SkeletonList rows={2} />
        ) : ask.error || !ask.data ? (
          <InlineError error={ask.error} />
        ) : (
          <>
            <div className="flex flex-col gap-1.5">
              <p className="eyebrow text-ink-2">The message each {label("staff")} gets</p>
              <p className="whitespace-pre-line border-l-[3px] border-line-strong pl-3 text-[13.5px] text-ink-2">
                {ask.data.messagePreview.replace("{name}", ask.data.staff[0]?.name.split(" ")[0] ?? "Rami")}
              </p>
              <p className="text-[12.5px] text-ink-3">
                {ask.data.whatsappReady
                  ? `A private WhatsApp from the Teamna number to each ${label("staff")}'s own mobile, and a push in the app. Nothing goes to a WhatsApp group. ${label("member", { plural: true, capital: true })} get nothing.`
                  : `WhatsApp is not connected here: Teamna records the ask and sends the push only.`}
              </p>
            </div>
            {ask.data.staff.length === 0 ? (
              <p className="text-[13.5px] text-ink-2">Add a {label("staff")} to the team below first.</p>
            ) : (
              <>
                <ul className="divide-y divide-line rounded-card border border-line">
                  {ask.data.staff.map((row) => (
                    <StaffRow
                      key={row.userId}
                      row={row}
                      busy={send.isPending && sentTo === row.userId}
                      onSend={() => run([row.userId], row.userId)}
                    />
                  ))}
                </ul>
                <div>
                  <Button loading={send.isPending && sentTo === "all"} onClick={() => run(undefined, "all")}>
                    Send to {ask.data.staff.length} {ask.data.staff.length === 1 ? label("staff") : staff}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
        <p className="text-[12.5px] text-ink-3">
          To stop {staff} adding {people}, turn off the module in Settings, Modules.
        </p>
      </div>
    </FormSection>
  );
}

const STATUS: Record<StaffRequestWhatsappStatus, string> = {
  sent: "sent on WhatsApp",
  failed: "WhatsApp did not send",
  no_phone: "no mobile, push only",
  not_configured: "push only",
};

function StaffRow({ row, busy, onSend }: { row: StaffAskRow; busy: boolean; onSend: () => void }) {
  const asked = row.lastAskedAt ? `Asked ${formatDayShort(row.lastAskedAt)}, ${STATUS[row.lastStatus ?? "sent"]}` : "Not asked yet";
  const added = row.added > 0 ? `${row.added} added${row.waiting > 0 ? `, ${row.waiting} to link` : ""}` : "";
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-2.5">
      <div className="min-w-0 flex-1">
        <p className="text-[14.5px] font-semibold text-ink">{row.name}</p>
        <p className="text-[12.5px] text-ink-2">
          {[row.groupNames.join(", "), asked, added].filter(Boolean).join(". ")}.
          {row.lastStatus === "failed" ? <span className="text-late-ink"> Check the mobile on WhatsApp.</span> : null}
        </p>
      </div>
      <Button variant="secondary" loading={busy} onClick={onSend}>
        {row.lastAskedAt ? "Remind" : "Send"}
      </Button>
    </li>
  );
}
